import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatStateContext } from '../contexts/chatStateContext';
import { LangContext } from '../contexts/langContext';
import { SocialContext } from '../contexts/socialContext';
import { StateContext } from '../contexts/stateContext';
import { ListAvatars } from './Lists/ListAvatars';
import { ListAvatarsLayout } from './Lists/ListAvatarsLayout';
import { Avatar } from './UiKit/Avatar';
import { AvatarName } from './UiKit/AvatarName';
import { Button } from './UiKit/Button';
import { SocialFilters, filterArrayByStats, matchesStatsFilter } from './UiKit/SocialFilters';
import { PopoverH } from './UiKit/TooltipH';

export function GroupMembers({ 
  group = {}, 
  members = [], 
  admins = [],
  mode = 'hd', 
  fullSrceen = false, 
  children,
  requestToJoin = () => void (0),
  fetchMembers,
  fetchGroup,
}) {
  const context = { ...useContext(SocialContext), ...useContext(StateContext), ...useContext(LangContext) };
  const chatState = useContext(ChatStateContext);
  const $ui = context.$ui;
  const navigate = useNavigate();

  const [filter, setFilter] = useState(mode === 'hd' ? 'types' : 'yoni');
  const [statsFilter, setStatsFilter] = useState(null);
  const invitation = Boolean(group.invitation);
  const admin = Array.isArray(admins) ? admins[0] : null;

  const sortedMembers = !members ? null : [
    ...(context.me && !invitation ? [context.me] : []),
    ...members.filter(one => one.avatar?.token !== context.me?.avatar?.token),
  ];

  const sortedMembersList = useMemo(() => {
    if (!sortedMembers) return [];
    return sortedMembers
  }, [members, context.me, filter, invitation]);

  const applyListStatsFilter = useCallback((array, listId) => {
    if (!statsFilter || statsFilter.listId !== listId) return array;
    return filterArrayByStats(array, statsFilter);
  }, [statsFilter]);

  const filteredMembers = useMemo(
    () => applyListStatsFilter(sortedMembersList, 'members'),
    [sortedMembersList, applyListStatsFilter]
  );

  const handleStatsSelect = (listId) => (match) => {
    setStatsFilter((prev) => {
      if (match.profile) {
        const sameProfile = prev?.listId === listId
          && prev?.type === match.type
          && prev?.profile?.[0] === match.profile[0]
          && prev?.profile?.[1] === match.profile[1];
        if (sameProfile) {
          return { listId, type: match.type };
        }
        return { listId, type: match.type, profile: match.profile };
      }
      if (match.type) {
        if (prev?.listId === listId && prev?.type === match.type) {
          return null;
        }
        return { listId, type: match.type };
      }
      if (prev?.listId === listId && matchesStatsFilter(prev, match)) {
        return null;
      }
      return { listId, ...match };
    });
  };

  const clearStatsFilter = useCallback(() => setStatsFilter(null), []);

  const toggleStatsMode = useCallback(() => {
    setFilter((prev) => (prev === 'types' ? 'profiles' : 'types'));
  }, []);

  const imAdmin = Boolean(admins.filter(u => u.user.token === context.me.user.token).length);

  return (
    <>
      {children}
      <div className="x_group_members0">
        {invitation && (
          <div className="x_group_invitation">
            <div className="x_group_invitation_inner _fv _fcc _fgap1">
              <div>
                {admin?.avatar
                  ? <PopoverH
                    title={<>
                      <div className="_fv _fgap06">
                        <Button
                          sm
                          ghost
                          icon='open_in_new'
                          onClick={() => navigate(`/u/${admin.user?.token || admin.avatar.token}`)}
                        >
                          {$ui('penta_open')}
                        </Button>
                        {admin.user?.token && (
                          <Button
                            sm
                            ghost
                            icon='mail'
                            onClick={() => chatState.setCurrentChat(admin.user.token)}
                          >
                            {$ui('chat_send_message')}
                          </Button>
                        )}
                      </div>
                    </>}
                  >
                    <span className="_f _fcc _fgap06" style={{ cursor: 'pointer' }}>
                      <Avatar
                        avatar={admin.avatar}
                        user={admin.user}
                        onClickOpen={false}
                        tooltip={false}
                        style={{ fontSize: '1.2em' }}
                      />
                      <b><AvatarName avatar={admin.avatar} /></b>
                    </span>
                  </PopoverH>
                  : <b>{$ui('group_admin')}</b>}
                {' '}{$ui('group_invitation_overlay')}:{' '}
                <b>{group.name}</b>
              </div>
              <Button onClick={() => requestToJoin(group.token)}>
                {$ui('group_askinto')}
              </Button>
              <Button ghost sm onClick={() => {
                context.x.leaveGroup(group.token);
              }}>
                {$ui('group_invitation_cancel')}
              </Button>
            </div>
          </div>
        )}
        <ListAvatarsLayout
          title=""
          listId='group'
          className={'v'}
          fullSrceen={fullSrceen}
          addLeft={<SocialFilters
            mode={mode}
            type={filter}
            array={sortedMembersList}
            onSelect={handleStatsSelect('members')}
            activeFilter={statsFilter?.listId === 'members' ? statsFilter : null}
            onClear={clearStatsFilter}
            onToggleMode={mode === 'hd' ? toggleStatsMode : undefined}
          />}
        >
          <ListAvatars
            mode={mode}
            listId={'group'}
            quantity={statsFilter?.listId === 'members' ? filteredMembers.length : sortedMembersList.length}
            array={filteredMembers}
            actions={[
              {
                noMe: imAdmin ? true : false,
                red: true,
                title: $ui('remove_from_group'),
                icon: 'person_remove',
                action: ({ user }) => context.x.removeUserFromGroup(group.token, user.token, () => {
                  fetchMembers?.();
                  fetchGroup?.();
                }),
              }
            ]}
            paginate={18}
            fullSrceen={fullSrceen}
          />
        </ListAvatarsLayout>
      </div>
    </>
  )
}
